import React, { useEffect, useState } from "react";
import {
  Input,
  Modal,
  Button,
  Popover,
  Layout,
  Skeleton,
  Table,
  Select,
  Pagination,
  DatePicker,
  Typography,
} from "antd";
import dayjs from "dayjs";
import {
  FaSearch,
  FaFilter,
  FaEye,
  FaPlus,
  FaBook,
  FaClock,
  FaCalendarAlt,
} from "react-icons/fa";
import { Get } from "../../config/api/get";
import { COURSE, CATEGORIES } from "../../config/constants/api";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ImageUrl } from "../../helpers/Functions";
import { message as messageApi } from "antd";
import "./course-management.css";

const { Option } = Select;

function CourseManagement() {
  const token = useSelector((state) => state.user.userToken);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedUser] = useState(null);
  const [courses, setCourses] = useState([]);
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 1,
    limit: 10,
    totalDocs: 0,
    totalPages: 0,
  });
  const navigate = useNavigate();

  const [filter, setFilter] = useState({
    status: null,
    keyword: "",
    from: null,
    to: null,
  });

  const startIndex =
    (paginationConfig.pageNumber - 1) * paginationConfig.limit + 1;
  const endIndex = Math.min(
    startIndex + paginationConfig.limit - 1,
    paginationConfig.totalDocs
  );
  const recordsMessage = `Showing ${startIndex} to ${endIndex} of ${paginationConfig.totalDocs} records`;

  useEffect(() => {
    getCourses();
    getCategories();
    // eslint-disable-next-line
  }, []);

  const getCategories = async () => {
    setLoading(true);
    const res = await Get(`${CATEGORIES.getAllcategories}`, token, {
      limit: "100",
    });

    setCategories(res.data.docs);
    setLoading(false);
  };

  const handlePageChange = (pageNumber) => {
    setPaginationConfig({
      ...paginationConfig,
      pageNumber: pageNumber,
    });

    getCourses(pageNumber);
  };

  const handleSearch = (value) => {
    setFilter({
      ...filter,
      keyword: value,
    });
  };

  const handleStatusChange = (value) => {
    setFilter({
      ...filter,
      category: value,
    });
  };

  const resetFilter = () => {
    setFilter({
      category: "",
      keyword: "",
      from: null,
      to: null,
    });
    getCourses(paginationConfig.pageNumber, paginationConfig.limit, "", true);
  };

  const handleOpenChange = (newOpen) => {
    setOpen(newOpen);
  };

  const handleFrom = (date) => {
    setFilter({
      ...filter,
      from: date,
    });
  };

  const handleTo = (date) => {
    setFilter({
      ...filter,
      to: date,
    });
  };

  const handleLimitChange = (pageSize) => {
    setPaginationConfig({
      ...paginationConfig,
      limit: pageSize,
      current: 1,
    });

    getCourses(1, pageSize);
  };

  const handleStatus = async () => {
    try {
      const index = courses.findIndex((user) => user._id === selectedUser._id);

      const response = await Get(
        COURSE.toggleStatus + "/" + selectedUser._id,
        token,
        {}
      );
      console.log(response);
      const newUsers = [...courses];

      newUsers[index].isActive = !selectedUser.isActive;
      setModalOpen(false);
      setCourses(newUsers);
    } catch (error) {
      console.log(error.message);
    }
  };

  const getCourses = async (pageNumber, pageSize, search, reset = false) => {
    setLoading(true);
    try {
      const response = await Get(COURSE.getAllCourses, token, {
        page: pageNumber
          ? pageNumber.toString()
          : paginationConfig.pageNumber.toString(),
        limit: pageSize
          ? pageSize.toString()
          : paginationConfig.limit.toString(),
        category: reset ? "" : filter.category || null,
        keyword: search ? search : null,
        from: reset ? "" : filter?.from ? filter?.from.toISOString() : "",
        to: reset ? "" : filter?.to ? filter?.to.toISOString() : "",
      });
      setLoading(false);
      if (response?.data?.docs) {
        setCourses(response?.data?.docs);
        setPaginationConfig({
          pageNumber: response?.data?.page,
          limit: response?.data?.limit,
          totalDocs: response?.data?.totalDocs,
          totalPages: response?.data?.totalPages,
        });
      } else {
        messageApi.error("Something went wrong!");
        console.log("error====>", response);
      }
    } catch (error) {
      console.log(error.message);
      setLoading(false);
    }
  };

  const itemRender = (_, type, originalElement) => {
    if (type === "prev") {
      return (
        <button type="button" className="cm-pager-btn">
          Previous
        </button>
      );
    }
    if (type === "next") {
      return (
        <button type="button" className="cm-pager-btn">
          Next
        </button>
      );
    }
    return originalElement;
  };

  const columns = [
    {
      title: "#",
      dataIndex: "key",
      key: "key",
      width: 70,
      render: (value, item, index) => (
        <span className="cm-index">{(index < 9 ? "0" : "") + (index + 1)}</span>
      ),
    },
    {
      title: "Course Code",
      dataIndex: "courseCode",
      key: "courseCode",
      render: (code) => <span className="cm-code">{code}</span>,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title) => (
        <span className="cm-title-cell" title={title}>
          {title}
        </span>
      ),
    },
    {
      title: "Category",
      dataIndex: "categories",
      key: "categories",
      render: (item) => (
        <span
          className="cm-category-cell"
          title={(item || []).map((c) => c.title).join(", ")}
        >
          {(item || []).map((cat, index, arr) => {
            return index !== arr.length - 1 ? cat.title + ", " : cat.title;
          })}
        </span>
      ),
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      render: (val) => (
        <span className="cm-meta-cell">
          <FaClock aria-hidden /> {val}
        </span>
      ),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      render: (item) => (
        <span className="cm-meta-cell">
          <FaCalendarAlt aria-hidden /> {dayjs(item).format("M/D/YYYY")}
        </span>
      ),
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (item) => <span className="cm-price">${item}</span>,
    },
    {
      title: "Action",
      dataIndex: "_id",
      key: "_id",
      width: 90,
      render: (item) => (
        <button
          type="button"
          className="cm-view-btn"
          aria-label="View course"
          onClick={() => navigate("/course-details/" + item)}
        >
          <FaEye aria-hidden="true" />
        </button>
      ),
    },
  ];

  const filterContent = (
    <div className="filterDropdown cm-filter-popover">
      <p className="cm-filter-head">Filter courses</p>
      <hr />

      <div className="filterDropdownBody">
        <p className="mainLabel">Start Date:</p>
        <DatePicker
          className="mainInput filterInput"
          value={filter.from}
          onChange={(e) => handleFrom(e)}
        />
        <DatePicker
          className="mainInput filterInput"
          value={filter.to}
          onChange={(e) => handleTo(e)}
        />

        <p className="mainLabel">Filter by Category:</p>

        <Select
          size={"large"}
          className="filterSelectBox cm-filter-select"
          placeholder="Select Category"
          value={filter.category}
          onChange={(e) => handleStatusChange(e)}
        >
          {categories.map((item) => (
            <Option key={item._id} value={item._id}>
              {item?.title}
            </Option>
          ))}
        </Select>

        <Button
          type="primary"
          block
          size={"large"}
          className="loginButton cm-filter-apply"
          onClick={() => getCourses()}
        >
          Apply
        </Button>
        <Button
          type="primary"
          block
          size={"large"}
          className="loginButton"
          onClick={() => resetFilter()}
        >
          Clear All
        </Button>
      </div>
    </div>
  );

  return (
    <Layout className="cm-page">
      <section
        className="cm-hero"
        style={{
          backgroundImage: `url(${ImageUrl("course-management-banner.png")})`,
        }}
      >
        <div className="cm-hero-overlay" aria-hidden />
        <div className="cm-wrap cm-hero-inner">
          <div className="cm-hero-copy">
            <span className="cm-hero-badge">COURSE MANAGEMENT</span>
            <h1 className="fontFamily1 cm-hero-title">
              Manage Courses. <span>Empower Learners.</span>
            </h1>
            <p className="fontFamily1 cm-hero-sub">
              Organize, update and manage your training courses all in one place
              with ease.
            </p>
            <div className="cm-hero-actions">
              <Button
                type="primary"
                size="large"
                className="cm-hero-btn-primary"
                onClick={() => navigate("/category")}
              >
                <span className="cm-hero-btn-plus">
                  <FaPlus />
                </span>
                Add Category
              </Button>
              <Button
                size="large"
                className="cm-hero-btn-outline"
                onClick={() => navigate("/course-details/add")}
              >
                <FaPlus /> Add Course
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="cm-wrap cm-main">
        <div className="cm-panel">
          <div className="cm-toolbar">
            <div className="cm-toolbar-intro">
              <span className="cm-toolbar-icon" aria-hidden>
                <FaBook />
              </span>
              <h2 className="fontFamily1">Course Management</h2>
            </div>
            <div className="cm-toolbar-actions">
              <Button
                type="primary"
                className="cm-btn-category"
                onClick={() => navigate("/category")}
              >
                Add Category
              </Button>
              <Button
                type="primary"
                className="cm-btn-course"
                onClick={() => navigate("/course-details/add")}
              >
                Add Course
              </Button>
            </div>
          </div>

          <div className="cm-sub-toolbar">
            <div className="cm-page-size">
              <span>Show</span>
              <Select
                size={"large"}
                className="cm-limit-select"
                defaultValue={paginationConfig.limit}
                onChange={(e) => handleLimitChange(e)}
                options={[
                  { value: 10, label: "10" },
                  { value: 20, label: "20" },
                  { value: 30, label: "30" },
                  { value: 40, label: "40" },
                  { value: 50, label: "50" },
                ]}
              />
              <span>entries</span>
            </div>
            <div className="cm-search-row">
              <Popover
                content={filterContent}
                trigger="click"
                open={open}
                onOpenChange={handleOpenChange}
                placement="bottomRight"
                arrow={false}
              >
                <Button className="cm-filter-trigger" aria-label="Open filters">
                  <FaFilter />
                </Button>
              </Popover>
              <div className="cm-search-wrap">
                <Input
                  className="cm-search"
                  placeholder="Search courses..."
                  value={filter.keyword}
                  onChange={(e) => handleSearch(e.target.value)}
                  onPressEnter={() =>
                    getCourses(1, paginationConfig.limit, filter.keyword)
                  }
                />
                <button
                  type="button"
                  className="cm-search-btn"
                  aria-label="Search courses"
                  onClick={() =>
                    getCourses(1, paginationConfig.limit, filter.keyword)
                  }
                >
                  <FaSearch />
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="cm-skeleton">
              <Skeleton active paragraph={{ rows: 6 }} />
            </div>
          ) : (
            <div className="cm-table-wrap">
              <Table
                className="cm-table"
                dataSource={courses}
                columns={columns}
                pagination={false}
                rowKey={(record, index) =>
                  record._id ?? record.courseCode ?? `course-row-${index}`
                }
                scroll={{ x: "max-content" }}
              />
            </div>
          )}

          <div className="cm-pagination-row">
            <p className="fontFamily1 cm-pagination-meta">{recordsMessage}</p>
            <Pagination
              className="styledPagination cm-pagination"
              onChange={(e) => handlePageChange(e)}
              current={parseInt(paginationConfig.pageNumber)}
              pageSize={paginationConfig.limit}
              total={paginationConfig.totalDocs}
              itemRender={itemRender}
            />
          </div>
        </div>
      </div>

      <Modal
        visible={modalOpen}
        onOk={() => handleStatus()}
        onCancel={() => setModalOpen(false)}
        okText="Yes"
        className="StyledModal"
        style={{
          left: 0,
          right: 0,
          marginLeft: "auto",
          marginRight: "auto",
          textAlign: "center",
        }}
        cancelText="No"
        cancelButtonProps={{
          style: {
            border: "2px solid #2484c6",
            color: "#2484c6",
            height: "auto",
            padding: "6px 35px",
            borderRadius: "50px",
            fontSize: "16px",
            marginTop: "15px",
          },
        }}
        okButtonProps={{
          style: {
            backgroundColor: "#2484c6",
            color: "white",
            marginTop: "15px",
            height: "auto",
            padding: "5px 35px",
            borderRadius: "50px",
            fontSize: "16px",
            border: "2px solid #2484c6",
          },
        }}
      >
        <Typography.Title level={4} style={{ fontSize: "25px" }}>
          {selectedUser?.isActive ? "Deactivate" : "Activate"}
        </Typography.Title>
        <Typography.Text style={{ fontSize: 16 }}>
          Do You Want To {selectedUser?.isActive ? "Deactivate" : "Activate"}{" "}
          This Service Provider?
        </Typography.Text>
      </Modal>
    </Layout>
  );
}

export default CourseManagement;
